import { useNavigate } from 'react-router-dom';
import type { Subscription } from './types';
import { SubscriptionIcon } from '@/shared/ui/SubscriptionIcon';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { cn } from '@/shared/lib/cn';
import { useT } from '@/shared/lib/i18n';

interface Props {
  sub: Subscription;
}

function periodText(period: { interval: number; unit: string }, t: ReturnType<typeof useT>): string {
  const u = period.unit === 'months'
    ? t('common.periodMonthsShort')
    : period.unit === 'weeks'
      ? t('common.periodWeeksShort')
      : t('common.periodDaysShort');
  return period.interval === 1 ? `1 ${u}` : `${period.interval} ${u}`;
}

const CURRENCY_SYMBOL: Record<string, string> = { RUB: '₽', USD: '$', EUR: '€' };

function statusBadge(status: Subscription['status'], t: ReturnType<typeof useT>) {
  switch (status) {
    case 'active': return null;
    case 'paused': return <StatusBadge variant="due" label={t('subscription.status.paused')} />;
    case 'closing': return <StatusBadge variant="confirm" label={t('subscription.status.closing')} />;
    case 'closed': return <StatusBadge variant="draft" label={t('subscription.status.closed')} />;
    default: return null;
  }
}

export function SubscriptionCardHolder({ sub }: Props) {
  const navigate = useNavigate();
  const t = useT();
  const sym = CURRENCY_SYMBOL[sub.currency] ?? sub.currency;
  const letter = sub.name.trim()[0]?.toUpperCase() ?? '?';
  const isEmoji = sub.icon.length > 0 && !/^[a-z0-9][a-z0-9-]*$/.test(sub.icon);
  const isSlug = sub.icon.length > 0 && /^[a-z0-9][a-z0-9-]*$/.test(sub.icon);

  return (
    <button
      className={cn(
        'w-full text-left rounded-[18px] p-4 transition-all active:scale-[0.98]',
        'bg-white/50 border border-white/60 backdrop-blur-[8px]',
        'shadow-[0_8px_22px_rgba(18,52,36,0.08)]',
      )}
      onClick={() => navigate(`/subscriptions/${sub.id}`)}
    >
      <div className="flex items-center gap-3">
        {/* Icon */}
        <div className="flex-shrink-0">
          {isEmoji ? (
            <span className="w-[44px] h-[44px] rounded-[12px] bg-brand-gradient flex items-center justify-center text-[24px] shadow-[0_4px_10px_rgba(18,120,90,0.28)] leading-none">
              {sub.icon}
            </span>
          ) : isSlug ? (
            <img
              src={`/api/v1/icons/${sub.icon}/default`}
              alt={sub.name}
              className="w-[44px] h-[44px] rounded-[12px] object-contain bg-white/60 border border-white/60 p-1"
            />
          ) : (
            <SubscriptionIcon letter={letter} size="lg" />
          )}
        </div>

        {/* Name + meta */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[16px] font-bold truncate">{sub.name}</p>
            {statusBadge(sub.status, t)}
          </div>
          <p className="text-[12px] text-text-secondary mt-0.5">
            {periodText(sub.period, t)} · {t('subscription.membersCountShort', { count: sub.member_count })}
          </p>
        </div>

        {/* Amount + chevron */}
        <div className="flex-shrink-0 text-right">
          <p className="text-[16px] font-bold font-mono tabular-nums">
            {Number(sub.total_price).toLocaleString('ru')} {sym}
          </p>
          <span className="text-text-muted text-[18px] leading-none">›</span>
        </div>
      </div>
    </button>
  );
}
